import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconAlertCircle, IconCircleCheck, IconLoader2, IconWand } from '@tabler/icons-react'
import { TYPICAL_SECONDS, agentMessage, displaySteps } from './generationSteps'
import { cx } from '../../utils/cx'
import classes from '../../styles/Generation.module.css'

function StepIcon({ state }) {
  const { t } = useTranslation()
  if (state === 'done') return <IconCircleCheck size={16} stroke={1.8} className={classes.stepDone} aria-label={t('editorV2.generation.stepState.done')} />
  if (state === 'failed') return <IconAlertCircle size={16} stroke={1.8} className={classes.stepFailed} aria-label={t('editorV2.generation.stepState.failed')} />
  return (
    <IconLoader2
      size={16}
      stroke={1.8}
      className={cx(classes.stepPending, state === 'active' && classes.stepActive)}
      aria-label={state === 'active' ? t('editorV2.generation.stepState.inProgress') : t('editorV2.generation.stepState.waiting')}
    />
  )
}

/**
 * The Figma "Loading AI Agent" card (492:30607) shown over the phone while the agent runs:
 * the six pipeline steps, the "Ad Agent / {model} - {pipeline}" header and a pink progress
 * bar. Completed rows get the green check, open rows the pink loader (spinning on the row
 * the agent is working on).
 */
/**
 * "Ad Agent / {model} - {pipeline}" and the pink progress bar: under the steps in the preview
 * card and in the Chat tab's generating state (Figma 226:18740).
 */
export function AdAgentProgress({ job, agentLabel, agentPipeline }) {
  const { t } = useTranslation()
  const running = !['completed', 'failed'].includes(job.status)
  const progress = job.status === 'completed' ? 100 : Math.max(0, Math.min(100, Number(job.progress) || 0))
  const subtitle = [agentLabel, agentPipeline].filter(Boolean).join(' - ') || t('editorV2.generation.agent.fastPipeline')
  return (
    <>
      <div className={classes.agentHeader}>
        <span className={classes.agentIcon} aria-hidden="true">
          <IconWand size={24} stroke={1.6} />
        </span>
        <span className={classes.agentTitle}>
          <span className={classes.agentName}>{t('editorV2.generation.agent.name')}</span>
          <span className={classes.agentModel}>{subtitle}</span>
        </span>
      </div>

      <div
        className={classes.agentBar}
        role="progressbar"
        aria-label={t('editorV2.generation.agent.progress')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        data-failed={job.status === 'failed' ? 'true' : undefined}
      >
        <span style={{ width: `${running && progress < 4 ? 4 : progress}%` }} />
      </div>
    </>
  )
}

/** Seconds since the run started, re-rendering once a second while it runs. */
function useElapsedSeconds(startedAt, running) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return undefined
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [running])
  return startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0
}

/** A thumbnail that disappears when the site refuses to serve it to the browser. */
function FoundImage({ src, className, alt }) {
  const [broken, setBroken] = useState(false)
  if (broken) return null
  return <img src={src} alt={alt} className={className} loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
}

/**
 * What the agent has found so far (the job status's `found`): the logo, the brand colours and
 * the best photos, each fading in when its step delivers it, so the wait shows progress.
 */
function FoundSoFar({ found }) {
  const { t } = useTranslation()
  const images = found?.images || []
  const colors = found?.colors || []
  if (!found?.logo && images.length === 0 && colors.length === 0) return null
  // One compact row: the card is pinned over the phone and must not grow off the screen.
  return (
    <div className={classes.agentFound} aria-label={found.site ? t('editorV2.generation.agent.foundSoFarOn', { site: found.site }) : t('editorV2.generation.agent.foundSoFar')}>
      {found.logo && (
        <span className={classes.agentFoundLogo}>
          <FoundImage src={found.logo} alt={t('editorV2.generation.agent.logo')} className={classes.agentFoundLogoImage} />
        </span>
      )}
      {images.slice(0, 3).map((src) => (
        <FoundImage key={src} src={src} alt="" className={classes.agentFoundPhoto} />
      ))}
      {colors.length > 0 && (
        <span className={classes.agentFoundColors}>
          {colors.map((color) => (
            <span key={color} className={classes.agentFoundColor} style={{ background: color }} title={color} />
          ))}
        </span>
      )}
    </div>
  )
}

export function AdAgentPopover({ job, agentLabel, agentPipeline, className }) {
  const { t } = useTranslation()
  const running = Boolean(job) && !['completed', 'failed'].includes(job.status)
  const elapsed = useElapsedSeconds(job?.startedAt, running)
  if (!job) return null

  const steps = displaySteps(job)
  const activeStep = steps.find((step) => step.state === 'active')

  return (
    <div className={cx(classes.agent, className)} role="status" aria-live="polite" aria-busy={running}>
      <ul className={classes.agentTasks}>
        {steps.map((step) => (
          <li key={step.key} className={classes.agentTask} data-state={step.state}>
            <span className={classes.agentTaskIcon}><StepIcon state={step.state} /></span>
            <span>{step.label}</span>
          </li>
        ))}
      </ul>

      {running && (
        <div className={classes.agentLive}>
          <p className={classes.agentMessage} key={agentMessage(activeStep?.key, job.found, elapsed)}>
            {agentMessage(activeStep?.key, job.found, elapsed)}
          </p>
          <p className={classes.agentEta}>
            {elapsed < TYPICAL_SECONDS
              ? t('editorV2.generation.agent.eta', { count: TYPICAL_SECONDS })
              : t('editorV2.generation.agent.almostThere')}
          </p>
        </div>
      )}

      {running && <FoundSoFar found={job.found} />}

      <AdAgentProgress job={job} agentLabel={agentLabel} agentPipeline={agentPipeline} />

      {job.status === 'failed' && job.error && (
        <p className={classes.agentError}>{job.error}</p>
      )}
    </div>
  )
}
